// src/components/InstantQuoteForm.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import "./InstantQuoteForm.css";
import { CONFIG } from "../config";
import useIsMobile from "../hooks/useIsMobile";
import { adjustFontSize } from "../utils/typography";
import {
  buildSubmissionFieldsFromFormData,
  getFieldSubmissionKey,
  notifySubmissionByEmail,
  saveFormSubmission,
} from "../utils/formSubmissions";

const IMAGES_BASE = CONFIG.IMAGES_BASE;

export default function InstantQuoteForm({
  formConfig,
  instantQuoteForm,
  loading,
  isSidebar = false,
  sideContent,
  fontStyles,
}) {
  const isMobile = useIsMobile();
  const [status, setStatus] = useState("");
  const [statusType, setStatusType] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ for transition (side content slide-in from right)
  const sideRef = useRef(null);
  const [sideShow, setSideShow] = useState(false);

  const settings = useMemo(() => {
    const hp = instantQuoteForm || {};
    const sc = sideContent || hp.sideContent || {};
    const sectionStyles = hp.sectionStyles || {};
    const useSideSectionColors = sc.useSectionColors !== false;

    const globalMainColors = {
      heading: fontStyles?.formTitle?.color || fontStyles?.heading?.color || "#111827",
      subHeading:
        fontStyles?.formDescription?.color ||
        fontStyles?.subHeading?.color ||
        "#374151",
      content: fontStyles?.formInput?.color || fontStyles?.content?.color || "#111827",
      buttonText: fontStyles?.button?.color || "#ffffff",
      buttonBg: fontStyles?.button?.backgroundColor || "#c79a2b",
    };

    const globalSideColors = {
      heading: fontStyles?.sideContentHeading?.color || fontStyles?.subHeading?.color || "#ffffff",
      highlight:
        fontStyles?.sideContentHighlight?.color || fontStyles?.heading?.color || "#d4af37",
      bullet: fontStyles?.sideContentBullet?.color || fontStyles?.content?.color || "#ffffff",
      bulletIcon:
        fontStyles?.sideContentHighlight?.color || fontStyles?.stats?.color || "#d4af37",
      footer: fontStyles?.sideContentFooter?.color || fontStyles?.subHeading?.color || "#ffffff",
    };

    return {
      display: hp.display ?? false,
      overlayOpacity: hp.overlayOpacity ?? 0.55,
      backgroundImage: hp.backgroundImage || "",
      backgroundColor: hp.backgroundColor || "transparent",

      sectionTitle:
        formConfig?.formTitle || hp.sectionTitle || "Get Instant Quotes",
      sectionDescription:
        formConfig?.formDetails ||
        hp.sectionDescription ||
        "Fill out the form below to get a free quote",

      titleColor: sectionStyles.headingColor || globalMainColors.heading,
      descriptionColor: sectionStyles.subHeadingColor || globalMainColors.subHeading,
      formBgColor: hp.formBgColor || formConfig?.cardBg || "#ffffff",
      formTextColor: sectionStyles.contentColor || globalMainColors.content,
      inputTextColor: sectionStyles.contentColor || globalMainColors.content,

      buttonColor: sectionStyles.buttonTextColor || globalMainColors.buttonText,
      buttonBgColor: sectionStyles.buttonBgColor || globalMainColors.buttonBg,
      buttonHoverColor: hp.buttonHoverColor || sectionStyles.buttonTextColor || globalMainColors.buttonText,
      buttonHoverBgColor:
        hp.buttonHoverBgColor || sectionStyles.buttonBgColor || globalMainColors.buttonBg,

      placeholderColor: formConfig?.inputPlaceholderColor || "#9ca3af",

      side: {
        enabled: sc.enabled ?? true,
        headingTop: sc.headingTop || "WHY CHOOSE",
        headingHighlight: sc.headingHighlight || "REGENCY INTERIOR",
        bullets: Array.isArray(sc.bullets) ? sc.bullets : [],
        footerLine: sc.footerLine || "REACH OUT TODAY TO DISCUSS YOUR VISION!",

        headingColor: useSideSectionColors
          ? sc.headingColor || globalSideColors.heading
          : globalSideColors.heading,
        highlightColor: useSideSectionColors
          ? sc.highlightColor || globalSideColors.highlight
          : globalSideColors.highlight,
        bulletColor: useSideSectionColors
          ? sc.bulletColor || globalSideColors.bullet
          : globalSideColors.bullet,
        bulletIconColor: useSideSectionColors
          ? sc.bulletIconColor || globalSideColors.bulletIcon
          : globalSideColors.bulletIcon,
        footerColor: useSideSectionColors
          ? sc.footerColor || globalSideColors.footer
          : globalSideColors.footer,
      },
    };
  }, [formConfig, instantQuoteForm, sideContent, fontStyles]);

  const sizeOverrides = instantQuoteForm?.sectionStyles?.fontSizes || {};

  const resolveFontSize = (value, fallback) => {
    const resolved =
      value !== undefined && value !== null && value !== "" ? value : fallback;
    return adjustFontSize(resolved, isMobile);
  };

  // ✅ IMPORTANT: compute render condition (NO early return yet)
  const canRender = !loading && !!formConfig && !!settings.display;

  // ✅ animate side content when in view (only when it will render)
  useEffect(() => {
    // Always call hook, but do nothing if it shouldn't run
    if (!canRender) return;
    if (isSidebar) return; // side content not shown

    const el = sideRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSideShow(true);
          io.disconnect();
        }
      },
      { threshold: 0.25 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [canRender, isSidebar]);

  // ✅ Now it's safe to early-return
  if (!canRender) return null;

  const bgUrl = settings.backgroundImage
    ? `${IMAGES_BASE}/${encodeURIComponent(settings.backgroundImage)}`
    : "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus("");

    const formData = new FormData(e.target);
    const configuredFields = Array.isArray(formConfig?.fields) ? formConfig.fields : [];
    const submissionFields = buildSubmissionFieldsFromFormData(formData, configuredFields);

    try {
      await saveFormSubmission({
        formId: formConfig?.id || instantQuoteForm?.formId || "",
        formName:
          formConfig?.formName || formConfig?.formTitle || settings.sectionTitle || "Instant Quote Form",
        formType: "instant_quote_form",
        fields: submissionFields,
        metadata: {
          placement: isSidebar ? "sidebar" : "homepage",
        },
      });

      let warning = "";
      try {
        await notifySubmissionByEmail({
          accessKey: formConfig?.accessKey,
          toEmail: formConfig?.notificationEmail,
          formName:
            formConfig?.formName ||
            formConfig?.formTitle ||
            settings.sectionTitle ||
            "Instant Quote Form",
          fields: submissionFields,
          metadata: {
            placement: isSidebar ? "sidebar" : "homepage",
            formType: "instant_quote_form",
          },
        });
      } catch (notifyError) {
        console.error("Instant quote email notification failed", notifyError);
        warning = " Saved, but email notification failed.";
      }

      setStatusType(warning ? "warning" : "success");
      setStatus(
        `${formConfig.submitMessage || "Thank you! We will get back to you soon."}${warning}`
      );
      e.target.reset();
    } catch (error) {
      console.error(error);
      setStatusType("error");
      setStatus("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ✅ Keep your dynamic CSS approach
  const formStyles = `
    .instant-form-input::placeholder { color: ${settings.placeholderColor} !important; opacity: 1 !important; }
    .instant-form-input::-webkit-input-placeholder { color: ${settings.placeholderColor} !important; opacity: 1 !important; }
    .instant-form-input::-moz-placeholder { color: ${settings.placeholderColor} !important; opacity: 1 !important; }
    .instant-form-input:-ms-input-placeholder { color: ${settings.placeholderColor} !important; opacity: 1 !important; }

    .instant-form-input:focus {
      outline: none !important;
      border-color: ${formConfig.inputBorderColor || "#d1d5db"} !important;
      box-shadow: 0 0 0 3px ${formConfig.inputBorderColor || "#d1d5db"}20 !important;
    }

    .instant-form-submit-btn { transition: all 0.3s ease !important; }
    .instant-form-submit-btn:hover:not(:disabled) {
      background-color: ${settings.buttonHoverBgColor} !important;
      color: ${settings.buttonHoverColor} !important;
      transform: translateY(-2px) !important;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
    }
    .instant-form-submit-btn:disabled { opacity: 0.6 !important; cursor: not-allowed !important; }

  `;

  return (
    <>
      <style>{formStyles}</style>

      <section
        className={`instant-form-section ${
          isSidebar ? "instant-form-sidebar" : ""
        }`}
        style={{
          backgroundImage: bgUrl ? `url(${bgUrl})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundColor: settings.backgroundColor,
          position: "relative",
          padding: "80px 20px",
        }}
      >
        <div
          className="instant-form-overlay"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: `rgba(0,0,0,${settings.overlayOpacity})`,
            zIndex: 1,
          }}
        />

        <div
          className="instant-form-container"
          style={{
            position: "relative",
            zIndex: 2,
            maxWidth: "1200px",
            margin: "0 auto",
          }}
        >
          <div
            className={`instant-form-grid ${
              isSidebar ? "instant-form-grid-sidebar" : ""
            }`}
            style={{
              display: "grid",
              gridTemplateColumns: isSidebar ? "1fr" : "1fr 1fr",
              gap: "60px",
              alignItems: "center",
            }}
          >
            {/* FORM */}
            <div
              className="instant-form-card"
              style={{
                backgroundColor: settings.formBgColor,
                color: settings.formTextColor,
                maxWidth: formConfig.cardWidth
                  ? `${formConfig.cardWidth}px`
                  : "520px",
                width: "100%",
                borderRadius: `${formConfig.cardRadius || 14}px`,
                padding: `${formConfig.cardPaddingTop || 22}px ${
                  formConfig.cardPaddingRight || 22
                }px ${formConfig.cardPaddingBottom || 22}px ${
                  formConfig.cardPaddingLeft || 22
                }px`,
                border: `1px solid ${formConfig.cardBorderColor || "#e5e7eb"}`,
                boxShadow:
                  formConfig.cardShadow || "0 24px 60px rgba(0,0,0,0.25)",
                margin: isSidebar ? "0 auto" : "0",
              }}
            >
              <h2
                className="instant-form-title"
                style={{
                  color: settings.titleColor,
                  margin: `0 0 ${formConfig.titleMarginBottom || 10}px 0`,
                  padding: `${formConfig.titlePaddingTop || 0}px ${
                    formConfig.titlePaddingRight || 0
                  }px ${formConfig.titlePaddingBottom || 0}px ${
                    formConfig.titlePaddingLeft || 0
                  }px`,
                  fontSize: resolveFontSize(
                    sizeOverrides.heading,
                    formConfig.titleFontSize || 22
                  ),
                  fontFamily: formConfig.titleFontFamily || "inherit",
                  fontWeight: formConfig.titleFontWeight || 800,
                  letterSpacing: `${formConfig.titleLetterSpacing || 0}px`,
                  lineHeight: formConfig.titleLineHeight || 1.15,
                  textTransform: formConfig.titleTextTransform || "none",
                  textAlign: formConfig.titleAlign || "left",
                }}
              >
                {settings.sectionTitle}
              </h2>

              <p
                className="instant-form-description"
                style={{
                  color: settings.descriptionColor,
                  margin: `0 0 ${formConfig.messageMarginBottom || 16}px 0`,
                  padding: `${formConfig.messagePaddingTop || 0}px ${
                    formConfig.messagePaddingRight || 0
                  }px ${formConfig.messagePaddingBottom || 0}px ${
                    formConfig.messagePaddingLeft || 0
                  }px`,
                  fontSize: resolveFontSize(
                    sizeOverrides.content,
                    formConfig.messageFontSize || 14
                  ),
                  fontFamily: formConfig.messageFontFamily || "inherit",
                  fontWeight: formConfig.messageFontWeight || 400,
                  letterSpacing: `${formConfig.messageLetterSpacing || 0}px`,
                  lineHeight: formConfig.messageLineHeight || 1.5,
                  textTransform: formConfig.messageTextTransform || "none",
                  textAlign: formConfig.messageAlign || "left",
                }}
              >
                {settings.sectionDescription}
              </p>

              <form
                onSubmit={handleSubmit}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: `${formConfig.fieldGap || 14}px`,
                }}
              >
                {(formConfig.fields || []).map((field, index) => {
                  const inputName = getFieldSubmissionKey(field, index);
                  const fieldType = field?.type || "text";
                  const isRequired = !!field?.required;

                  const commonInputStyles = {
                    color: settings.inputTextColor,
                    backgroundColor: formConfig.inputBg || "#ffffff",
                    border: `${formConfig.inputBorderWidth || 1}px solid ${
                      formConfig.inputBorderColor || "#d1d5db"
                    }`,
                    borderRadius: `${formConfig.inputRadius || 0}px`,
                    padding: `${formConfig.inputPaddingTop || 12}px ${
                      formConfig.inputPaddingRight || 14
                    }px ${formConfig.inputPaddingBottom || 12}px ${
                      formConfig.inputPaddingLeft || 14
                    }px`,
                    fontSize: resolveFontSize(
                      formConfig.inputFontSize,
                      14
                    ),
                    fontFamily: formConfig.inputFontFamily || "inherit",
                    fontWeight: formConfig.inputFontWeight || 400,
                    letterSpacing: `${formConfig.inputLetterSpacing || 0}px`,
                    lineHeight: formConfig.inputLineHeight || 1.5,
                    margin: `${formConfig.inputMarginTop || 0}px ${
                      formConfig.inputMarginRight || 0
                    }px ${formConfig.inputMarginBottom || 0}px ${
                      formConfig.inputMarginLeft || 0
                    }px`,
                    width: "100%",
                    boxSizing: "border-box",
                  };

                  let fieldInput = (
                    <input
                      className="instant-form-input"
                      type={fieldType}
                      name={inputName}
                      placeholder={field.placeholder || field.name}
                      required={isRequired}
                      disabled={isSubmitting}
                      style={{
                        ...commonInputStyles,
                        height: `${formConfig.inputHeight || 44}px`,
                      }}
                    />
                  );

                  if (fieldType === "textarea") {
                    fieldInput = (
                      <textarea
                        className="instant-form-input"
                        name={inputName}
                        placeholder={field.placeholder || field.name}
                        required={isRequired}
                        disabled={isSubmitting}
                        rows={4}
                        style={{
                          ...commonInputStyles,
                          minHeight: "110px",
                          resize: "vertical",
                        }}
                      />
                    );
                  } else if (fieldType === "select") {
                    const options = Array.isArray(field.options) ? field.options : [];
                    fieldInput = (
                      <select
                        className="instant-form-input"
                        name={inputName}
                        defaultValue=""
                        required={isRequired}
                        disabled={isSubmitting}
                        style={{
                          ...commonInputStyles,
                          height: `${formConfig.inputHeight || 44}px`,
                        }}
                      >
                        <option value="" disabled>
                          {field.placeholder || `Select ${field.name || "option"}`}
                        </option>
                        {options.map((option, optionIndex) => (
                          <option key={`${inputName}-${optionIndex}`} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    );
                  }

                  return (
                  <div key={field.id || inputName} className="instant-form-field">
                    {formConfig.showLabels && (
                      <label
                        style={{
                          display: "block",
                          marginBottom: `${formConfig.labelMarginBottom || 6}px`,
                          color: settings.formTextColor,
                          fontSize: resolveFontSize(
                            formConfig.labelFontSize,
                            13
                          ),
                          fontWeight: formConfig.labelFontWeight || 600,
                          fontFamily: formConfig.titleFontFamily || "inherit",
                        }}
                      >
                        {field.name}
                        {field.required ? " *" : ""}
                      </label>
                    )}

                    {fieldInput}
                  </div>
                )})}

                <div
                  style={{
                    display: "flex",
                    justifyContent:
                      formConfig.buttonAlign === "center"
                        ? "center"
                        : formConfig.buttonAlign === "right"
                        ? "flex-end"
                        : "flex-start",
                    width: "100%",
                    marginTop: "10px",
                  }}
                >
                  <button
                    className="instant-form-submit-btn"
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      backgroundColor: settings.buttonBgColor,
                      color: settings.buttonColor,
                      border: "none",
                      borderRadius: `${formConfig.buttonRadius || 4}px`,
                      padding: `${formConfig.buttonPaddingTop || 12}px ${
                        formConfig.buttonPaddingRight || 18
                      }px ${formConfig.buttonPaddingBottom || 12}px ${
                        formConfig.buttonPaddingLeft || 18
                      }px`,
                      fontSize: resolveFontSize(
                        sizeOverrides.button,
                        formConfig.buttonFontSize || 14
                      ),
                      fontFamily: formConfig.buttonFontFamily || "inherit",
                      fontWeight: formConfig.buttonFontWeight || 700,
                      letterSpacing: `${formConfig.buttonLetterSpacing || 0}px`,
                      lineHeight: formConfig.buttonLineHeight || 1.5,
                      height: `${formConfig.buttonHeight || 44}px`,
                      minWidth: `${formConfig.buttonMinWidth || 120}px`,
                      width: formConfig.buttonWidth === "full" ? "100%" : "auto",
                      margin: `${formConfig.buttonMarginTop || 0}px ${
                        formConfig.buttonMarginRight || 0
                      }px ${formConfig.buttonMarginBottom || 0}px ${
                        formConfig.buttonMarginLeft || 0
                      }px`,
                      cursor: isSubmitting ? "not-allowed" : "pointer",
                      opacity: isSubmitting ? 0.7 : 1,
                    }}
                  >
                    {isSubmitting
                      ? "Submitting..."
                      : formConfig.buttonText || "Submit"}
                  </button>
                </div>

                {status && (
                  <div
                    className="instant-form-status"
                    style={{
                      marginTop: "15px",
                      padding: "10px",
                      backgroundColor:
                        statusType === "error"
                          ? "#dc2626"
                          : statusType === "warning"
                          ? "#b45309"
                          : "#10b981",
                      color: "#ffffff",
                      borderRadius: "4px",
                      textAlign: "center",
                      fontSize: resolveFontSize(
                        formConfig.messageFontSize,
                        14
                      ),
                      fontFamily: formConfig.messageFontFamily || "inherit",
                      fontWeight: formConfig.messageFontWeight || 400,
                    }}
                  >
                    {status}
                  </div>
                )}
              </form>
            </div>

            {/* SIDE CONTENT */}
            {!isSidebar && settings.side.enabled && (
              <div
                ref={sideRef}
                className={`instant-form-side-content ${
                  sideShow ? "side-show" : ""
                }`}
                style={{ padding: "20px" }}
              >
                <div
                  className="instant-form-side-heading-top"
                  style={{
                    color: settings.side.headingColor,
                    fontSize: resolveFontSize(
                      sizeOverrides.sideHeading,
                      "14px"
                    ),
                    fontWeight: "600",
                    letterSpacing: "2px",
                    marginBottom: "10px",
                    fontFamily: formConfig.titleFontFamily || "inherit",
                    textTransform: "uppercase",
                  }}
                >
                  {settings.side.headingTop}
                </div>

                <div
                  className="instant-form-side-highlight"
                  style={{
                    color: settings.side.highlightColor,
                    fontSize: resolveFontSize(
                      sizeOverrides.sideHighlight,
                      "32px"
                    ),
                    fontWeight: "700",
                    marginBottom: "30px",
                    lineHeight: "1.2",
                    fontFamily: formConfig.titleFontFamily || "inherit",
                  }}
                >
                  {settings.side.headingHighlight}
                </div>

                <ul
                  className="instant-form-side-list"
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: "0 0 30px 0",
                    fontFamily: formConfig.messageFontFamily || "inherit",
                    color: settings.side.bulletColor,
                  }}
                >
                  {settings.side.bullets.map((bullet, index) => (
                    <li
                      key={index}
                      style={{
                        marginBottom: "15px",
                        paddingLeft: "25px",
                        position: "relative",
                        fontSize: resolveFontSize(
                          sizeOverrides.sideBullet,
                          "16px"
                        ),
                        lineHeight: "1.5",
                      }}
                    >
                      <span
                        style={{
                          color: settings.side.bulletIconColor,
                          position: "absolute",
                          left: 0,
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: "24px",
                          fontWeight: "bold",
                        }}
                      >
                        •
                      </span>
                      {bullet}
                    </li>
                  ))}
                </ul>

                <div
                  className="instant-form-side-footer"
                  style={{
                    color: settings.side.footerColor,
                    fontSize: resolveFontSize(
                      sizeOverrides.sideFooter,
                      "14px"
                    ),
                    fontWeight: "600",
                    letterSpacing: "1px",
                    marginTop: "20px",
                    paddingTop: "20px",
                    borderTop: "none",
                    fontFamily: formConfig.titleFontFamily || "inherit",
                    textTransform: "uppercase",
                  }}
                >
                  {settings.side.footerLine}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
